import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Tooltip,
  Chip,
  Button,
  TextField,
  IconButton,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  RemoveCircleOutline as ScaleDownIcon,
  DeleteOutline as ReleaseIcon,
  Warning as WarningIcon,
  CleaningServices as ReclaimIcon,
  Terminal as TerminalIcon,
} from '@mui/icons-material';
import { AzureReservationSummary, AzureReservationItem } from '../../types/providers';
import SshTerminalDialog from './SshTerminalDialog';

type ChipColor = 'success' | 'info' | 'warning' | 'error' | 'default' | 'primary';

const STATUS_META: Record<string, { label: string; color: ChipColor }> = {
  free: { label: 'Free', color: 'success' },
  claimed: { label: 'Claimed', color: 'info' },
  assigned: { label: 'Assigned', color: 'primary' },
  releasing: { label: 'Releasing', color: 'warning' },
  failed: { label: 'Failed', color: 'error' },
};

// Order matters: how categories stack in the segmented bar and breakdown chips.
const CATEGORIES: Array<{ key: keyof AzureReservationSummary; status: string }> = [
  { key: 'free', status: 'free' },
  { key: 'claimed', status: 'claimed' },
  { key: 'assigned', status: 'assigned' },
  { key: 'releasing', status: 'releasing' },
  { key: 'failed', status: 'failed' },
];

const SEGMENT_COLOR: Record<string, string> = {
  free: 'success.main',
  claimed: 'info.main',
  assigned: 'primary.main',
  releasing: 'warning.main',
  failed: 'error.main',
};

// Returns null for empty input, NaN for invalid input, or the positive integer.
const parseCount = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n <= 0) return NaN;
  return n;
};

interface SummaryCellProgress {
  phase: string;
  completed: number;
  total: number;
}

const ProgressLine: React.FC<{ progress: SummaryCellProgress }> = ({ progress }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    <CircularProgress size={11} />
    <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
      {PHASE_LABEL[progress.phase] ?? 'Working'}
      {progress.total > 0 ? ` ${progress.completed}/${progress.total}` : '…'}
    </Typography>
  </Box>
);

/**
 * Compact reservation readout for the main region row: total + free counts and a
 * segmented utilisation bar. Surfaces failed reservations in red and an in-flight
 * operation's progress (e.g. "Reserving 3/10").
 */
export const ReservationSummaryCell: React.FC<{
  summary?: AzureReservationSummary | null;
  progress?: SummaryCellProgress | null;
}> = ({ summary, progress }) => {
  if (!summary) {
    return progress ? (
      <ProgressLine progress={progress} />
    ) : (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );
  }
  if (summary.total === 0 && !progress) {
    return (
      <Typography variant="caption" color="text.secondary">
        None reserved
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 130 }}>
      {progress && <ProgressLine progress={progress} />}
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {summary.total}
        </Typography>
        <Typography variant="caption" color="success.main">
          {summary.free} free
        </Typography>
        {summary.failed > 0 && (
          <Typography variant="caption" color="error.main" sx={{ fontWeight: 600 }}>
            · {summary.failed} failed
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', backgroundColor: 'action.hover' }}>
        {CATEGORIES.map(({ key, status }) => {
          const count = (summary[key] as number) || 0;
          if (count <= 0) return null;
          return (
            <Box
              key={status}
              sx={{ flex: count, backgroundColor: SEGMENT_COLOR[status] }}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export interface ReservationProgress {
  phase: string;
  completed: number;
  total: number;
}

const PHASE_LABEL: Record<string, string> = {
  reserve: 'Reserving',
  scale_down: 'Recycling',
  recycle: 'Recycling',
  release: 'Releasing',
  reclaim: 'Reclaiming',
};

interface ReservationManagerProps {
  summary?: AzureReservationSummary | null;
  busy?: boolean;
  progress?: ReservationProgress | null;
  onReserve: (count: number, includeIpv6: boolean) => void;
  onScaleDown: (count: number) => void;
  onRelease: (count: number | undefined) => void;
  onReleaseSelected: (ids: string[]) => void;
  onReclaim?: () => void;
  onRefresh: () => void;
}

const ItemStatusChip: React.FC<{ status: string }> = ({ status }) => {
  const meta = STATUS_META[status] ?? { label: status, color: 'default' as ChipColor };
  return <Chip label={meta.label} color={meta.color} size="small" variant="outlined" />;
};

const truncate = (value?: string | null, head = 8): string => {
  if (!value) return '—';
  return value.length > head + 4 ? `${value.slice(0, head)}…${value.slice(-4)}` : value;
};

// A machine is reachable over SSH only once it has been claimed/assigned (the VM
// is booted) and has a public IPv4 to target.
const canSsh = (item: AzureReservationItem): boolean =>
  !!item.pip_address && (item.status === 'claimed' || item.status === 'assigned');

const ItemsTable: React.FC<{
  items: AzureReservationItem[];
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onToggleAll: (ids: string[], select: boolean) => void;
}> = ({ items, selectedIds, onToggle, onToggleAll }) => {
  const [sshTarget, setSshTarget] = useState<AzureReservationItem | null>(null);

  // Only free slots are releasable, so only they are selectable.
  const freeIds = useMemo(() => items.filter((i) => i.status === 'free').map((i) => i.id), [items]);
  const allFreeSelected = freeIds.length > 0 && freeIds.every((id) => selectedIds.has(id));
  const someFreeSelected = freeIds.some((id) => selectedIds.has(id));

  if (items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        No reservation slots yet. Use “Reserve” to pre-provision NIC/IP capacity.
      </Typography>
    );
  }
  return (
    <>
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 280 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Tooltip title="Select all free slots">
                  <span>
                    <Checkbox
                      size="small"
                      disabled={freeIds.length === 0}
                      checked={allFreeSelected}
                      indeterminate={someFreeSelected && !allFreeSelected}
                      onChange={(e) => onToggleAll(freeIds, e.target.checked)}
                    />
                  </span>
                </Tooltip>
              </TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Mode</TableCell>
              <TableCell>Private IP</TableCell>
              <TableCell>Public IPv4</TableCell>
              <TableCell>Public IPv6</TableCell>
              <TableCell>Claimed by</TableCell>
              <TableCell align="center">SSH</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => {
              const selectable = item.status === 'free';
              return (
              <TableRow
                key={item.id}
                hover
                selected={selectedIds.has(item.id)}
                onClick={selectable ? () => onToggle(item.id) : undefined}
                sx={selectable ? { cursor: 'pointer' } : undefined}
              >
                <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    size="small"
                    disabled={!selectable}
                    checked={selectedIds.has(item.id)}
                    onChange={() => onToggle(item.id)}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ItemStatusChip status={item.status} />
                    {item.last_error && (
                      <Tooltip title={item.last_error}>
                        <WarningIcon color="error" fontSize="small" />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">{item.ip_mode === 'dualstack' ? 'IPv4+IPv6' : 'IPv4'}</Typography>
                </TableCell>
                <TableCell sx={{ fontFamily: '"Space Mono", monospace', fontSize: '0.75rem' }}>
                  {item.private_ip || '—'}
                </TableCell>
                <TableCell sx={{ fontFamily: '"Space Mono", monospace', fontSize: '0.75rem' }}>
                  {item.pip_address || '—'}
                </TableCell>
                <TableCell sx={{ fontFamily: '"Space Mono", monospace', fontSize: '0.75rem' }}>
                  {item.pip_v6_address || '—'}
                </TableCell>
                <TableCell>
                  <Tooltip title={item.claimed_by_bot_id || ''}>
                    <Typography variant="caption" sx={{ fontFamily: '"Space Mono", monospace' }}>
                      {truncate(item.claimed_by_bot_id)}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell align="center" sx={{ p: 0.25 }}>
                  <Tooltip
                    title={
                      canSsh(item)
                        ? `Open SSH terminal to ${item.pip_address}`
                        : 'SSH available once the machine is claimed and has a public IPv4'
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        color="primary"
                        disabled={!canSsh(item)}
                        onClick={() => setSshTarget(item)}
                        aria-label="open ssh terminal"
                      >
                        <TerminalIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <SshTerminalDialog
        open={!!sshTarget}
        host={sshTarget?.pip_address || ''}
        label={sshTarget?.claimed_by_bot_id || sshTarget?.pip_address || undefined}
        onClose={() => setSshTarget(null)}
      />
    </>
  );
};

/**
 * Full reservation management UI shown inside a region's expanded detail panel.
 * Replaces the old window.prompt()-driven reserve/scale-down/release flow with
 * themed, validated inline forms plus a breakdown and per-slot inspector.
 */
const ReservationManager: React.FC<ReservationManagerProps> = ({
  summary,
  busy = false,
  progress,
  onReserve,
  onScaleDown,
  onRelease,
  onReleaseSelected,
  onReclaim,
  onRefresh,
}) => {
  const [reserveCount, setReserveCount] = useState('1');
  const [includeIpv6, setIncludeIpv6] = useState(false);
  const [scaleDownCount, setScaleDownCount] = useState('1');
  const [releaseCount, setReleaseCount] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const items = useMemo(() => summary?.items ?? [], [summary?.items]);

  // Drop selections whose slot is no longer free/present (e.g. after a refresh or a
  // release completing) so the "Release Selected" count and payload stay accurate.
  useEffect(() => {
    const freeIds = new Set(items.filter((i) => i.status === 'free').map((i) => i.id));
    setSelectedIds((prev) => {
      const next = new Set(Array.from(prev).filter((id) => freeIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [items]);

  const toggleSelected = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAllSelected = (ids: string[], select: boolean) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (select ? next.add(id) : next.delete(id)));
      return next;
    });

  const reserveValue = parseCount(reserveCount);
  const scaleDownValue = parseCount(scaleDownCount);
  const releaseRaw = releaseCount.trim();
  const releaseValue = releaseRaw === '' ? undefined : parseCount(releaseCount);

  const reserveInvalid = reserveValue === null || Number.isNaN(reserveValue);
  const scaleDownInvalid = scaleDownValue === null || Number.isNaN(scaleDownValue);
  const releaseInvalid = releaseRaw !== '' && (releaseValue === null || Number.isNaN(releaseValue));

  const free = summary?.free ?? 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Breakdown */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" sx={{ mr: 0.5 }}>
          Network reservations
        </Typography>
        <Chip label={`${summary?.total ?? 0} total`} size="small" />
        {CATEGORIES.map(({ key, status }) => {
          const count = (summary?.[key] as number) || 0;
          if (count <= 0) return null;
          const meta = STATUS_META[status];
          return <Chip key={status} label={`${count} ${meta.label.toLowerCase()}`} color={meta.color} size="small" variant="outlined" />;
        })}
        <Box sx={{ flexGrow: 1 }} />
        {onReclaim && (summary?.total ?? 0) > (summary?.free ?? 0) && (
          <Tooltip title="Clear reservations whose claiming bot was terminated or deleted (their Azure resources are already gone)">
            <span>
              <Button
                size="small"
                variant="text"
                color="error"
                startIcon={<ReclaimIcon fontSize="small" />}
                onClick={onReclaim}
                disabled={busy}
              >
                Reclaim orphaned
              </Button>
            </span>
          </Tooltip>
        )}
        <Tooltip title="Refresh reservations">
          <span>
            <IconButton size="small" onClick={onRefresh} disabled={busy}>
              {busy ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* In-progress indicator */}
      {busy && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.75,
            p: 1.25,
            borderRadius: 1,
            backgroundColor: 'action.hover',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {progress ? PHASE_LABEL[progress.phase] ?? 'Working' : 'Working'}
              {progress && progress.total > 0 ? ` ${progress.completed}/${progress.total}` : '…'}
            </Typography>
          </Box>
          <LinearProgress
            variant={progress && progress.total > 0 ? 'determinate' : 'indeterminate'}
            value={
              progress && progress.total > 0
                ? Math.min((progress.completed / progress.total) * 100, 100)
                : undefined
            }
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>
      )}

      {/* Action forms */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
          alignItems: 'flex-start',
        }}
      >
        <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 220 }}>
          <Typography variant="caption" color="text.secondary">
            Pre-provision new NIC/IP slots
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              label="Count"
              type="number"
              size="small"
              value={reserveCount}
              onChange={(e) => setReserveCount(e.target.value)}
              error={reserveInvalid}
              inputProps={{ min: 1 }}
              sx={{ width: 90 }}
            />
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              disabled={busy || reserveInvalid}
              onClick={() => !reserveInvalid && onReserve(reserveValue as number, includeIpv6)}
            >
              Reserve
            </Button>
          </Box>
          <FormControlLabel
            control={<Checkbox size="small" checked={includeIpv6} onChange={(e) => setIncludeIpv6(e.target.checked)} />}
            label={<Typography variant="caption">Include IPv6 public IP</Typography>}
          />
        </Paper>

        <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 220 }}>
          <Typography variant="caption" color="text.secondary">
            Recycle (reboot, keep warm) free slots · {free} free
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              label="Count"
              type="number"
              size="small"
              value={scaleDownCount}
              onChange={(e) => setScaleDownCount(e.target.value)}
              error={scaleDownInvalid}
              inputProps={{ min: 1 }}
              sx={{ width: 90 }}
            />
            <Button
              variant="outlined"
              color="warning"
              size="small"
              startIcon={<ScaleDownIcon />}
              disabled={busy || scaleDownInvalid || free === 0}
              onClick={() => !scaleDownInvalid && onScaleDown(scaleDownValue as number)}
            >
              Recycle
            </Button>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 220 }}>
          <Typography variant="caption" color="text.secondary">
            Delete free slots permanently (blank = all)
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              label="Count"
              type="number"
              size="small"
              placeholder="all"
              value={releaseCount}
              onChange={(e) => setReleaseCount(e.target.value)}
              error={releaseInvalid}
              inputProps={{ min: 1 }}
              sx={{ width: 90 }}
            />
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<ReleaseIcon />}
              disabled={busy || releaseInvalid || free === 0}
              onClick={() => !releaseInvalid && onRelease(releaseValue === null ? undefined : (releaseValue as number | undefined))}
            >
              Release
            </Button>
          </Box>
          <Tooltip title="Delete the free slots checked in the table below">
            <span>
              <Button
                fullWidth
                variant="outlined"
                color="error"
                size="small"
                startIcon={<ReleaseIcon />}
                disabled={busy || selectedIds.size === 0}
                onClick={() => selectedIds.size > 0 && onReleaseSelected(Array.from(selectedIds))}
              >
                Release selected{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
              </Button>
            </span>
          </Tooltip>
        </Paper>
      </Box>

      <ItemsTable
        items={items}
        selectedIds={selectedIds}
        onToggle={toggleSelected}
        onToggleAll={toggleAllSelected}
      />
    </Box>
  );
};

export default ReservationManager;
